import { FC } from 'react';
import { Formik, Form } from 'formik';
import { Box } from '@mui/material';

import { HotelSearchFormData, HOTEL_SEARCH_INITIAL_VALUES } from '../../types/hotelSearch.types';

import styles from './HotelSearchForm.module.scss';
import CustomReactDatePicker from '../../../../../common/CustomReactDatePicker';
import CustomButton from '../../../../../common/CustomButton';
import HotelLocationField from './HotelLocationField';
import GuestsSelect from './GuestsSelect';
import { SearchIcon } from '../../../../../components/Icons';

type HotelSearchFormProps = {
  onSubmit?: (values: HotelSearchFormData) => void | Promise<void>;
  isLoading?: boolean;
  isMobileLayout?: boolean;
};

const HotelSearchForm: FC<HotelSearchFormProps> = ({ onSubmit, isLoading = false, isMobileLayout = false }) => {
  const validate = (values: HotelSearchFormData) => {
    const errors: Partial<Record<keyof HotelSearchFormData, string>> = {};

    if (!values.location.trim()) {
      errors.location = 'Location is required';
    }

    if (!values.checkIn) {
      errors.checkIn = 'Check-in date is required';
    }

    if (!values.checkOut) {
      errors.checkOut = 'Check-out date is required';
    }

    if (values.checkIn && values.checkOut && values.checkOut < values.checkIn) {
      errors.checkOut = 'Check-out must be on or after check-in';
    }

    return errors;
  };

  return (
    <Box className={styles.hotelSearchFormContainer}>
      <Formik
        initialValues={HOTEL_SEARCH_INITIAL_VALUES}
        validate={validate}
        onSubmit={async (values, { resetForm }) => {
          await Promise.resolve(onSubmit?.(values));
          resetForm({
            values: { ...values },
            submitCount: 0,
            touched: {},
            errors: {},
          });
        }}
        enableReinitialize
      >
        {({ values, handleChange, errors, submitCount, setFieldValue }) => {
          const showSubmitErrors = submitCount > 0;
          const reserveDesktopSlots = !isMobileLayout;

          return (
            <Form className={styles.tripForm}>
              <Box className={styles.formGrid}>
                <Box className={styles.formField}>
                  <HotelLocationField
                    name="location"
                    value={values.location}
                    onChange={handleChange}
                    error={showSubmitErrors && errors.location ? errors.location : undefined}
                    compactForMobile={isMobileLayout}
                    allowClear
                    onPlaceSelected={details => {
                      if (!details) {
                        setFieldValue('location_details', undefined, true);
                        return;
                      }
                      setFieldValue(
                        'location_details',
                        {
                          countryCode: details.countryCode,
                          city: details.city,
                          latitude: details.latitude,
                          longitude: details.longitude,
                          placeId: details.placeId,
                        },
                        true
                      );
                    }}
                  />
                </Box>

                <Box className={styles.formField}>
                  <CustomReactDatePicker
                    name="checkIn"
                    inputLabel="Check-in"
                    value={values.checkIn}
                    onChange={handleChange}
                    error={showSubmitErrors && !!errors.checkIn}
                    helperText={showSubmitErrors && errors.checkIn ? errors.checkIn : ''}
                    dateFormat="MMM d, yyyy"
                    calendarPreset="flightDiscover"
                    reserveHelperSpace={reserveDesktopSlots}
                    compactForMobile={isMobileLayout}
                    flightRange={{
                      rangeRole: 'start',
                      startIso: values.checkIn,
                      endIso: values.checkOut,
                    }}
                    allowClear
                  />
                </Box>

                <Box className={styles.formField}>
                  <CustomReactDatePicker
                    name="checkOut"
                    inputLabel="Check-out"
                    value={values.checkOut}
                    onChange={handleChange}
                    error={showSubmitErrors && !!errors.checkOut}
                    helperText={showSubmitErrors && errors.checkOut ? errors.checkOut : ''}
                    min={values.checkIn}
                    dateFormat="MMM d, yyyy"
                    calendarPreset="flightDiscover"
                    reserveHelperSpace={reserveDesktopSlots}
                    compactForMobile={isMobileLayout}
                    flightRange={{
                      rangeRole: 'end',
                      startIso: values.checkIn,
                      endIso: values.checkOut,
                    }}
                    allowClear
                  />
                </Box>

                <Box className={styles.formField}>
                  <GuestsSelect
                    guests={values.guests}
                    onGuestsChange={n => setFieldValue('guests', n, true)}
                    compactForMobile={isMobileLayout}
                  />
                  {reserveDesktopSlots ? (
                    <Box
                      className={styles.fieldErrorReserve}
                      aria-hidden
                    />
                  ) : null}
                </Box>

                <Box
                  className={`${styles.submitButtonWrapper} ${
                    isMobileLayout ? styles.submitButtonWrapperWide : ''
                  }`}
                >
                  <CustomButton
                    btnType="submit"
                    variant="contained"
                    loading={isLoading}
                    startIcon={<SearchIcon color="#FFFFFF" size={24} />}
                  >
                    {isMobileLayout ? 'Hotels Search' : ''}
                  </CustomButton>
                  {reserveDesktopSlots ? (
                    <Box
                      className={styles.fieldErrorReserve}
                      aria-hidden
                    />
                  ) : null}
                </Box>
              </Box>
            </Form>
          );
        }}
      </Formik>
    </Box>
  );
};

export default HotelSearchForm;
